import api from './client';

export type CorrespondenceType = 'BEFORE_PACK' | 'AFTER_PACK';
export type ReviewableDocument = 'LABEL' | 'PACKING_SLIP';

/** 1 = approved, the documents match the order. 2 = an issue was found. */
export type Acknowledgement = 1 | 2;

export interface CorrespondencePhoto {
  url: string;
  caption: string | null;
  uploadedBy?: string;
  uploadedAt?: string;
}

export interface TrackingCorrespondence {
  id: string;
  type: CorrespondenceType;
  acknowledged: Acknowledgement | null;
  documents: string[];
  comments: string | null;
  photos: CorrespondencePhoto[];
  createdAt: string;
  user: { userId: string; name: string | null; email: string | null };
}

export interface ReportPerson {
  userId: string;
  name: string | null;
  email: string | null;
  at: string | null;
}

export interface ShipmentTrackingReport {
  trackId: string;
  trackingNumber: string;
  shipmentId: string;
  orderId: string | null;
  /** Fulfilment status - PENDING, PICKED, PACKED, CARRIER_SCANNED. */
  status: string;
  internalHold: boolean;
  holdReason: string | null;
  /** The latest before-pack review is an approval: the facility may act. */
  beforePackApproved: boolean;
  /** The label has had its one print and no override is open. */
  labelPrintLocked: boolean;
  /** `active` until the reprint uses it; reason and user stay as the record. */
  printOverride: { active: boolean; reason: string | null; by: ReportPerson | null };
  picker: ReportPerson | null;
  packer: ReportPerson | null;
  packingPhotos: CorrespondencePhoto[];
  printCounts: { label: number; packingSlip: number; brochure: number };
  /** The office's downloads of the paperwork from the order page. */
  downloads: { label: ReportPerson | null; packingSlip: ReportPerson | null };
  /**
   * Why a before-pack acknowledgement cannot be filed yet - both documents have
   * to be downloaded first - in the server's words. Null once they have been.
   */
  beforePackReviewBlockedReason: string | null;
  correspondences: TrackingCorrespondence[];
}

export type PaperworkDocument = 'LABEL' | 'PACKING_SLIP' | 'BROCHURE';

/** One download (office) or print (facility) of one document. */
export interface PaperworkEvent {
  id: string;
  trackingNumber: string;
  document: PaperworkDocument;
  action: 'DOWNLOAD' | 'PRINT';
  /** TRACK_HISTORY, ORDER_PAGE, OFFICE, FACILITY, or BACKFILL (from before the history existed). */
  source: string | null;
  at: string;
  by: string | null;
  email: string | null;
}

export interface PaperworkParcel {
  trackingNumber: string;
  shipmentId: string;
  documents: Record<
    PaperworkDocument,
    { downloads: number; prints: number; last: { action: string; at: string; by: string | null } | null }
  >;
  /** Facility prints counted before the history existed - no person or time for them. */
  earlierPrints: Record<PaperworkDocument, number>;
}

export interface PaperworkHistory {
  /** Newest first. */
  events: PaperworkEvent[];
  parcels: PaperworkParcel[];
}

/** Where a download is made from, for the paperwork history. */
export const currentDownloadSource = (): 'TRACK_HISTORY' | 'ORDER_PAGE' | 'OFFICE' => {
  const p = typeof window !== 'undefined' ? window.location.pathname : '';
  if (p.startsWith('/track-history')) return 'TRACK_HISTORY';
  if (p.startsWith('/external-orders')) return 'ORDER_PAGE';
  return 'OFFICE';
};

/** One file the download endpoint hands back for the browser to save. */
export interface DownloadFile {
  document: ReviewableDocument | 'COMBINED' | 'BROCHURE';
  filename: string;
  contentType: string;
  base64: string;
}

export interface DownloadResult {
  files: DownloadFile[];
  unavailable: { document: ReviewableDocument; reason: string }[];
  /** Things worth knowing that did not stop a file: "the label is ZPL, saved on its own". */
  notes: string[];
  report: ShipmentTrackingReport;
}

/** How much of what was asked for made it into one document's joined PDF. */
export interface BulkDownloadSummary {
  document: ReviewableDocument;
  requested: number;
  included: number;
  /** Tracking numbers whose page is not in the PDF, in the order asked. */
  missing: string[];
}

/** Several parcels at once: one joined PDF per document, no single report. */
export type BulkDownloadResult = Omit<DownloadResult, 'report'> & {
  summary: BulkDownloadSummary[];
};

export interface CorrespondenceInput {
  type: CorrespondenceType;
  acknowledged: Acknowledgement;
  documents: ReviewableDocument[];
  comments?: string;
  photos?: { url: string; caption?: string }[];
}

const path = (trackingNumber: string) =>
  `/shipment-tracking-reports/${encodeURIComponent(trackingNumber)}`;

export const shipmentTrackingReports = {
  getReport: (trackingNumber: string): Promise<ShipmentTrackingReport> =>
    api.get(path(trackingNumber)).then((res) => res.data),

  /** PENDING -> PICKED, recording the signed-in user as the picker. */
  markPicked: (trackingNumber: string): Promise<ShipmentTrackingReport> =>
    api.post(`${path(trackingNumber)}/pick`).then((res) => res.data),

  /**
   * PICKED -> PACKED, recording the signed-in user as the packer. Any photos
   * (already uploaded, passed as URLs) are appended to the parcel's packing photos.
   */
  markPacked: (
    trackingNumber: string,
    photos: { url: string }[] = [],
  ): Promise<ShipmentTrackingReport> =>
    api.post(`${path(trackingNumber)}/pack`, { photos }).then((res) => res.data),

  /**
   * Put a parcel on internal hold (a reason is required) or release it. The
   * fulfilment status is untouched; a held parcel cannot be picked or packed.
   */
  setHold: (
    trackingNumber: string,
    hold: boolean,
    reason?: string,
  ): Promise<ShipmentTrackingReport> =>
    api.post(`${path(trackingNumber)}/hold`, { hold, reason }).then((res) => res.data),

  /**
   * Allow the label one more print. The reprint switches the override back off,
   * so one override is one copy.
   */
  overridePrint: (trackingNumber: string, reason: string): Promise<ShipmentTrackingReport> =>
    api.post(`${path(trackingNumber)}/print-override`, { reason }).then((res) => res.data),

  /**
   * Upload photos and get their URLs back.
   *
   * Correspondence and packing photos are both stored as URLs, so this runs
   * first and its result is what addCorrespondence or markPacked is given.
   */
  uploadCorrespondencePhotos: (
    trackingNumber: string,
    files: File[],
  ): Promise<{ photos: { url: string }[] }> => {
    const formData = new FormData();
    files.forEach((file) => formData.append('photos', file));
    return api
      .post(`${path(trackingNumber)}/correspondence/photos`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((res) => res.data);
  },

  /**
   * The label and/or packing slip as separate files, recording the download
   * against the parcel. `combined` with both documents asks for one PDF - label,
   * then packing slip - and `includeBrochure` adds the company brochure after them.
   * Office roles only; the facility prints through the Facility Shipments grid.
   */
  downloadDocuments: (
    trackingNumber: string,
    documents: ReviewableDocument[],
    combined = false,
    includeBrochure = false,
    /** PRINT when it goes to the print dialog - recorded in the order history. */
    intent?: 'PRINT',
  ): Promise<DownloadResult> =>
    api
      .post(`${path(trackingNumber)}/download`, {
        documents,
        combined,
        includeBrochure,
        source: currentDownloadSource(),
        intent,
      })
      .then((res) => res.data),

  /**
   * The labels, or the packing slips, of several parcels joined into one PDF,
   * pages in the order given. Each parcel's download is recorded exactly as
   * downloadDocuments records it. `all` makes it ONE PDF of everything: each
   * parcel's label then its packing slip, then the brochure once at the end.
   */
  downloadDocumentsBulk: (
    trackingNumbers: string[],
    documents: ReviewableDocument[],
    all = false,
    /** For the order history: printed or saved, and what was picked. */
    history?: { intent?: 'PRINT'; scope?: 'ORDER' | 'SHIPMENT' | 'PARCEL' },
    /**
     * With `all`, the tracking numbers of each order when several are in one
     * PDF: the brochure then goes after each order instead of once at the end.
     */
    groups?: string[][],
    /** checksheet false: no Final Checksheet at the end (one order's own Print all / Download all). */
    options?: { checksheet?: boolean },
  ): Promise<BulkDownloadResult> =>
    api
      .post('/shipment-tracking-reports/download', {
        trackingNumbers,
        documents,
        all,
        source: currentDownloadSource(),
        ...history,
        ...(groups ? { groups } : {}),
        ...(options?.checksheet === false ? { checksheet: false } : {}),
      })
      .then((res) => res.data),

  /** Every download and print of one parcel's paperwork, with per-document counts. */
  getDocumentHistory: (trackingNumber: string): Promise<PaperworkHistory> =>
    api.get(`${path(trackingNumber)}/document-history`).then((res) => res.data),

  /** The same for every parcel on an order. */
  getOrderDocumentHistory: (orderId: string): Promise<PaperworkHistory> =>
    api
      .get(`/shipment-tracking-reports/orders/${encodeURIComponent(orderId)}/document-history`)
      .then((res) => res.data),

  /** File one review. Returns the whole report, new review included. */
  addCorrespondence: (
    trackingNumber: string,
    input: CorrespondenceInput,
  ): Promise<ShipmentTrackingReport> =>
    api.post(`${path(trackingNumber)}/correspondence`, input).then((res) => res.data),
};
